// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  Policy,
  PolicyCreateDTO,
  PolicyUpdateDTO,
  PolicyRelationship,
  PolicyRelationshipCreateDTO,
  PolicyRelationshipUpdateDTO,
  PolicyOwnershipChangeDTO,
  PolicyOwnershipCompatibilityDTO,
  PolicyOwnershipStats
} from '../types/policy';
import type { PageResponse } from '../types/client';

export interface PolicyDeletionEligibility {
  eligible: boolean;
  blockingReasons: string[];
}

export interface PolicyDeleteRequest {
  reason: string;
}

export const policyApi = {
  /**
   * Get all policies for a profile (paginated)
   */
  getPolicies: async (profileId: string, page: number = 0, size: number = 20): Promise<PageResponse<Policy>> => {
    const response = await api.get<PageResponse<Policy>>(`/profiles/${profileId}/policies`, {
      params: { page, size }
    });
    return response.data;
  },

  /**
   * Get all policies for a profile (non-paginated, for LOA creation)
   */
  getPoliciesByProfile: async (profileId: string): Promise<Policy[]> => {
    const response = await api.get<PageResponse<Policy>>(`/profiles/${profileId}/policies`, {
      params: { page: 0, size: 1000 } // Large size to get all policies
    });
    return response.data.content;
  },

  /**
   * Get single policy by ID
   */
  getPolicy: async (profileId: string, policyId: string): Promise<Policy> => {
    const response = await api.get<Policy>(`/profiles/${profileId}/policies/${policyId}`);
    return response.data;
  },

  /**
   * Create new policy for profile
   */
  createPolicy: async (profileId: string, data: PolicyCreateDTO): Promise<Policy> => {
    const response = await api.post<Policy>(`/profiles/${profileId}/policies`, data);
    return response.data;
  },

  /**
   * Update policy
   */
  updatePolicy: async (profileId: string, policyId: string, data: PolicyUpdateDTO): Promise<Policy> => {
    const response = await api.put<Policy>(`/profiles/${profileId}/policies/${policyId}`, data);
    return response.data;
  },

  /**
   * Update policy status
   */
  updatePolicyStatus: async (profileId: string, policyId: string, status: string): Promise<Policy> => {
    const response = await api.patch<Policy>(`/profiles/${profileId}/policies/${policyId}/status`, null, {
      params: { status }
    });
    return response.data;
  },

  /**
   * Check if a policy is eligible for deletion
   */
  checkDeletionEligibility: async (profileId: string, policyId: string): Promise<PolicyDeletionEligibility> => {
    const response = await api.get<PolicyDeletionEligibility>(`/profiles/${profileId}/policies/${policyId}/deletion-eligibility`);
    return response.data;
  },

  /**
   * Delete policy (with reason)
   */
  deletePolicy: async (profileId: string, policyId: string, reason: string): Promise<void> => {
    await api.delete(`/profiles/${profileId}/policies/${policyId}`, {
      params: { reason },
    });
  },

  /**
   * Upload a document for a policy (multipart). Reuses the existing
   * PolicyDocumentController endpoint + tenant document storage flow.
   */
  uploadPolicyDocument: async (policyId: string, file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    await api.post(`/policies/${policyId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Get all relationships for a policy
   */
  getRelationships: async (policyId: string): Promise<PolicyRelationship[]> => {
    const response = await api.get<PolicyRelationship[]>(`/policies/${policyId}/relationships`);
    return response.data;
  },

  /**
   * Get owners for a policy
   */
  getOwners: async (policyId: string): Promise<PolicyRelationship[]> => {
    const response = await api.get<PolicyRelationship[]>(`/policies/${policyId}/relationships/owners`);
    return response.data;
  },

  /**
   * Create relationship between policy and client
   */
  createRelationship: async (policyId: string, data: PolicyRelationshipCreateDTO): Promise<PolicyRelationship> => {
    const response = await api.post<PolicyRelationship>(`/policies/${policyId}/relationships`, data);
    return response.data;
  },

  /**
   * Update relationship
   */
  updateRelationship: async (policyId: string, relationshipId: string, data: PolicyRelationshipUpdateDTO): Promise<PolicyRelationship> => {
    const response = await api.put<PolicyRelationship>(`/policies/${policyId}/relationships/${relationshipId}`, data);
    return response.data;
  },

  /**
   * Delete relationship
   */
  deleteRelationship: async (policyId: string, relationshipId: string): Promise<void> => {
    await api.delete(`/policies/${policyId}/relationships/${relationshipId}`);
  },

  // ========== Policy Ownership Type Management ==========

  /**
   * Change policy ownership type
   */
  changeOwnershipType: async (
    profileId: string,
    policyId: string,
    data: PolicyOwnershipChangeDTO
  ): Promise<Policy> => {
    const response = await api.put<Policy>(
      `/profiles/${profileId}/policies/${policyId}/ownership`,
      data
    );
    return response.data;
  },

  /**
   * Check ownership type compatibility
   */
  checkOwnershipCompatibility: async (
    profileId: string,
    policyId: string,
    targetOwnershipType: string
  ): Promise<PolicyOwnershipCompatibilityDTO> => {
    const response = await api.get<PolicyOwnershipCompatibilityDTO>(
      `/profiles/${profileId}/policies/${policyId}/ownership/compatibility`,
      { params: { targetOwnershipType } }
    );
    return response.data;
  },

  /**
   * Get ownership type statistics for brokerage
   */
  getOwnershipStats: async (profileId: string): Promise<PolicyOwnershipStats> => {
    const response = await api.get<PolicyOwnershipStats>(
      `/profiles/${profileId}/policies/ownership-stats`
    );
    return response.data;
  }
};
